import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../../../components/dashboard/Layout';
import CreateFolderModal from '../../../components/projects/CreateFolderModal';
import CreateFileModal from '../../../components/projects/CreateFileModal';
import ContextMenu from '../../../components/common/ContextMenu';

interface Folder {
  id: string;
  name: string;
  created_at: string;
}

interface File {
  id: string;
  folder_id: string | null;
  name: string;
  content: string | null;
  created_at: string;
}

interface ProjectDetails {
  id: string;
  name: string;
  script_content: string | null;
  whitelisted_users: number;
  created_at: string;
  folders: Folder[];
  files: File[];
}

export default function ProjectDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState<ProjectDetails | null>(null);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isCreateFolderModalOpen, setIsCreateFolderModalOpen] = useState(false);
  const [isCreateFileModalOpen, setIsCreateFileModalOpen] = useState(false);
  const [selectedFolder, setSelectedFolder] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(new Set());
  const [contextMenu, setContextMenu] = useState<{
    x: number;
    y: number;
    type: 'background' | 'folder' | 'file';
    targetId?: string;
  } | null>(null);

  useEffect(() => {
    fetchProjectDetails();
  }, [id]);

  const fetchProjectDetails = async () => {
    if (!id) return;
    
    try {
      setIsLoading(true);
      setError('');
      const response = await fetch(`/api/projects/${id}`);
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to fetch project details');
      }

      setProject(data);
    } catch (err) {
      console.error('Error fetching project:', err);
      setError(err instanceof Error ? err.message : 'Failed to load project details');
      // Redirect back to projects page after error
      setTimeout(() => navigate('/dashboard/projects'), 2000);
    } finally {
      setIsLoading(false);
    }
  };

  const toggleFolder = (folderId: string) => {
    setExpandedFolders(prev => {
      const next = new Set(prev);
      if (next.has(folderId)) {
        next.delete(folderId);
      } else {
        next.add(folderId);
      }
      return next;
    });
  };

  const handleCreateFolder = async (name: string) => {
    try {
      const response = await fetch(`/api/projects/${id}/folders`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ name }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message || 'Failed to create folder');
      }

      fetchProjectDetails();
      setIsCreateFolderModalOpen(false);
    } catch (err) {
      console.error('Error creating folder:', err);
      setError(err instanceof Error ? err.message : 'Failed to create folder');
    }
  };

  const handleCreateFile = async (name: string, content: string) => {
    try {
      // Get the current folder context
      const folder_id = selectedFolder;
      
      const response = await fetch(`/api/projects/${id}/files`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name,
          content,
          folder_id
        }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message || 'Failed to create file');
      }

      // After successful creation, expand the parent folder if it exists
      if (folder_id) {
        setExpandedFolders(prev => new Set([...prev, folder_id]));
      }

      fetchProjectDetails();
      setIsCreateFileModalOpen(false);
    } catch (err) {
      console.error('Error creating file:', err);
      setError(err instanceof Error ? err.message : 'Failed to create file');
    }
  };

  const handleContextMenu = (
    e: React.MouseEvent,
    type: 'background' | 'folder' | 'file',
    id?: string
  ) => {
    e.preventDefault();
    setContextMenu({
      x: e.clientX,
      y: e.clientY,
      type,
      targetId: id,
    });
  };

  const getContextMenuItems = () => {
    if (!contextMenu) return [];

    switch (contextMenu.type) {
      case 'background':
        return [
          {
            label: 'New Folder',
            icon: '📁',
            onClick: () => {
              setSelectedFolder(null);
              setIsCreateFolderModalOpen(true);
            },
          },
          {
            label: 'New File',
            icon: '📄',
            onClick: () => {
              setSelectedFolder(null);
              setIsCreateFileModalOpen(true);
            },
          },
        ];
      case 'folder':
        return [
          {
            label: 'New File Here',
            icon: '📄',
            onClick: () => {
              setSelectedFolder(contextMenu.targetId!);
              setIsCreateFileModalOpen(true);
            },
          },
          {
            label: 'Delete Folder',
            icon: '🗑️',
            onClick: () => handleDeleteFolder(contextMenu.targetId!),
            color: 'text-red-400',
          },
        ];
      case 'file':
        return [
          {
            label: 'Delete File',
            icon: '🗑️',
            onClick: () => handleDeleteFile(contextMenu.targetId!),
            color: 'text-red-400',
          },
        ];
      default:
        return [];
    }
  };

  const handleDeleteFolder = async (folderId: string) => {
    if (!confirm('Are you sure you want to delete this folder and all its contents?')) return;

    try {
      const response = await fetch(`/api/projects/${id}/folders/${folderId}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message || 'Failed to delete folder');
      }

      fetchProjectDetails();
    } catch (err) {
      console.error('Error deleting folder:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete folder');
    }
  };

  const handleDeleteFile = async (fileId: string) => {
    if (!confirm('Are you sure you want to delete this file?')) return;

    try {
      const response = await fetch(`/api/projects/${id}/files/${fileId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to delete file');
      }

      // Clear selected file if it was the one deleted
      if (selectedFile?.id === fileId) {
        setSelectedFile(null);
      }

      // Refresh project details
      await fetchProjectDetails();

      // Show success message
      const tempDiv = document.createElement('div');
      tempDiv.className = 'fixed bottom-4 right-4 bg-green-500 text-white px-4 py-2 rounded shadow-lg';
      tempDiv.textContent = 'File deleted successfully';
      document.body.appendChild(tempDiv);
      
      setTimeout(() => {
        document.body.removeChild(tempDiv);
      }, 2000);

    } catch (err) {
      console.error('Error deleting file:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete file');
    }
  };

  const handleCopyLoader = async (fileId: string) => {
    try {
      const response = await fetch(`/api/projects/${id}/files/${fileId}/loader`);
      const data = await response.json();

      if (!response.ok) throw new Error(data.message);

      // Use the setclipboard function from the executor
      setclipboard(data.loader);
      
      // Show a temporary success message
      const tempDiv = document.createElement('div');
      tempDiv.className = 'fixed bottom-4 right-4 bg-green-500 text-white px-4 py-2 rounded shadow-lg';
      tempDiv.textContent = 'Loader script copied to clipboard!';
      document.body.appendChild(tempDiv);
      
      setTimeout(() => {
        document.body.removeChild(tempDiv);
      }, 2000);
    } catch (err) {
      console.error('Error copying loader:', err);
      setError(err instanceof Error ? err.message : 'Failed to copy loader');
    }
  };

  const renderFileExplorer = () => {
    if (!project) return null;

    return (
      <div 
        className="space-y-1"
        onContextMenu={(e) => handleContextMenu(e, 'background')}
      >
        {/* Root level files */}
        {project.files
          .filter(file => !file.folder_id)
          .map(file => (
            <div
              key={file.id}
              className={`flex items-center p-2 rounded cursor-pointer ${
                selectedFile?.id === file.id ? 'bg-gray-600' : 'hover:bg-gray-700'
              }`}
              onClick={() => setSelectedFile(file)}
              onContextMenu={(e) => {
                e.stopPropagation();
                handleContextMenu(e, 'file', file.id);
              }}
            >
              <span className="text-blue-400 mr-2">📄</span>
              <span className="text-gray-300">{file.name}</span>
            </div>
          ))}

        {/* Folders */}
        {project.folders.map(folder => (
          <div 
            key={folder.id} 
            className="space-y-1"
            onContextMenu={(e) => {
              e.stopPropagation();
              handleContextMenu(e, 'folder', folder.id);
            }}
          >
            <div
              className={`flex items-center p-2 rounded cursor-pointer ${
                selectedFolder === folder.id ? 'bg-gray-600' : 'hover:bg-gray-700'
              }`}
              onClick={() => toggleFolder(folder.id)}
            >
              <span className="text-yellow-400 mr-2">
                {expandedFolders.has(folder.id) ? '📂' : '📁'}
              </span>
              <span className="text-gray-300">{folder.name}</span>
            </div>

            {/* Folder contents */}
            {expandedFolders.has(folder.id) && (
              <div className="ml-4 space-y-1">
                {project.files
                  .filter(file => file.folder_id === folder.id)
                  .map(file => (
                    <div
                      key={file.id}
                      className={`flex items-center p-2 rounded cursor-pointer ${
                        selectedFile?.id === file.id ? 'bg-gray-600' : 'hover:bg-gray-700'
                      }`}
                      onClick={() => setSelectedFile(file)}
                      onContextMenu={(e) => {
                        e.stopPropagation();
                        handleContextMenu(e, 'file', file.id);
                      }}
                    >
                      <span className="text-blue-400 mr-2">📄</span>
                      <span className="text-gray-300">{file.name}</span>
                    </div>
                  ))}
              </div>
            )}
          </div>
        ))}
      </div>
    );
  };

  if (error) {
    return (
      <Layout>
        <div className="p-6">
          <div className="text-red-500">{error}</div>
        </div>
      </Layout>
    );
  }

  if (isLoading) {
    return (
      <Layout>
        <div className="p-6">
          <div className="text-white">Loading project details...</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/dashboard/projects')}
              className="text-gray-400 hover:text-white"
            >
              ←
            </button>
            <h1 className="text-2xl font-bold text-white">{project?.name}</h1>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* File Explorer */}
          <div className="bg-gray-800 rounded-lg p-4">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-white font-semibold">Files</h2>
              <div className="flex gap-2">
                <button
                  onClick={() => setIsCreateFolderModalOpen(true)}
                  className="text-sm text-blue-400 hover:text-blue-300"
                >
                  New Folder
                </button>
                <button
                  onClick={() => {
                    setSelectedFolder(null);
                    setIsCreateFileModalOpen(true);
                  }}
                  className="text-sm text-blue-400 hover:text-blue-300"
                >
                  New File
                </button>
              </div>
            </div>
            {renderFileExplorer()}
          </div>

          {/* Content Viewer */}
          <div className="lg:col-span-2 bg-gray-800 rounded-lg p-4">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-white font-semibold">
                {selectedFile ? selectedFile.name : 'Content'}
              </h2>
            </div>
            {selectedFile ? (
              <pre className="bg-gray-900 p-4 rounded overflow-auto text-gray-300 font-mono text-sm h-[calc(100vh-300px)]">
                {selectedFile.content}
              </pre>
            ) : (
              <div className="text-center text-gray-400 py-8">
                Select a file to view its content
              </div>
            )}
          </div>
        </div>
      </div>

      <CreateFolderModal
        isOpen={isCreateFolderModalOpen}
        onClose={() => setIsCreateFolderModalOpen(false)}
        onCreate={handleCreateFolder}
      />
      <CreateFileModal
        isOpen={isCreateFileModalOpen}
        onClose={() => setIsCreateFileModalOpen(false)}
        onCreate={handleCreateFile}
      />
      {contextMenu && (
        <ContextMenu
          x={contextMenu.x}
          y={contextMenu.y}
          items={getContextMenuItems()}
          onClose={() => setContextMenu(null)}
        />
      )}
    </Layout>
  );
} 